import React, { useState, useMemo } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, Card, Typography, Row, Col, Tabs, DatePicker, Switch, Avatar, Divider } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { EyeOutlined, CheckOutlined, CloseOutlined, EditOutlined, SearchOutlined, BankOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { apiService } from '../../../services/api';
import { ApiDefinition } from '../../../config';
import TabListLoading from '../../../components/Common/TabListLoading';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface AdminCompany {
  id: number;
  companyName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  taxNumber: string;
  authorizedPerson: string;
  website?: string;
  companySize?: string;
  industry?: string;
  description?: string;
  logo?: string;
  firmStatus: number; // 0: Pending, 1: Approved, 2: Rejected
  approvalDate?: string;
  subscriptionType: number; // 0: Free, 1: Premium
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isSubscriptionActive: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

const parseCompaniesResponse = (response: any) => {
  const rawData = response?.data ?? response?.Data ?? response;
  const items = Array.isArray(rawData) ? rawData : rawData?.data ?? rawData?.Data ?? [];
  const total = rawData?.totalCount ?? rawData?.TotalCount ?? response?.totalCount ?? response?.TotalCount ?? 0;
  return { items: Array.isArray(items) ? items : [], total };
};

const CompanyListTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<AdminCompany | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);

  const firmStatus = useMemo(() => {
    if (activeTab === 'pending') return 0;
    if (activeTab === 'approved') return 1;
    if (activeTab === 'rejected') return 2;
    return undefined;
  }, [activeTab]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, ApiDefinition.searchDebounceMs);

  const { data, isLoading: loading } = useQuery({
    queryKey: adminQueryKeys.companies(currentPage, pageSize, firmStatus, debouncedSearchTerm.trim() || undefined, sortBy, sortOrder),
    queryFn: async () => {
      const r = await apiService.getAdminCompanies(currentPage, pageSize, firmStatus, debouncedSearchTerm.trim() || undefined, sortBy, sortOrder);
      const { items, total } = parseCompaniesResponse(r);
      return { items: items as AdminCompany[], total };
    },
    staleTime: 1000 * 60 * 2,
  });
  const companies = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const updateStatusMutation = useMutation({
    mutationFn: ({ companyId, status }: { companyId: number; status: number }) => apiService.updateCompanyStatus(companyId, status),
    onSuccess: (res, variables) => {
      if (res.success) {
        toast.success(variables.status === 1 ? 'Firma onaylandı' : variables.status === 2 ? 'Firma reddedildi' : 'Firma onay bekliyor durumuna alındı');
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'companies'] });
      } else toast.error(res.message || 'İşlem başarısız');
    },
    onError: () => toast.error('İşlem sırasında hata oluştu'),
  });

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => apiService.updateAdminCompany(id, payload),
    onSuccess: (res) => {
      if (res.success) {
        setEditModalVisible(false);
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'companies'] });
        toast.success('Firma başarıyla güncellendi');
      } else toast.error(res.message || 'Firma güncellenirken hata oluştu');
    },
    onError: () => toast.error('Firma güncellenirken hata oluştu'),
  });

  const handleApprove = (companyId: number) => {
    updateStatusMutation.mutate({ companyId, status: 1 });
  };

  const handleReject = (companyId: number) => {
    updateStatusMutation.mutate({ companyId, status: 2 });
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter?.columnKey != null && sorter?.order != null) {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';
      const field = sorter.columnKey === 'companyName' ? 'companyName' : sorter.columnKey === 'email' ? 'email' : 'createdAt';
      setSortBy(field);
      setSortOrder(order);
      setCurrentPage(1);
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  const handlePending = (companyId: number) => {
    updateStatusMutation.mutate({ companyId, status: 0 });
  };

  const handleViewDetail = (company: AdminCompany) => {
    setSelectedCompany(company);
    setDetailModalVisible(true);
  };

  const handleEdit = (company: AdminCompany) => {
    setSelectedCompany(company);
    form.setFieldsValue({
      ...company,
      subscriptionStartDate: company.subscriptionStartDate ? dayjs(company.subscriptionStartDate) : null,
      subscriptionEndDate: company.subscriptionEndDate ? dayjs(company.subscriptionEndDate) : null
    });
    setEditModalVisible(true);
  };

  const handleUpdate = (values: any) => {
    if (!selectedCompany?.id) return;
    
    // SubscriptionType'ı enum değerine çevir (0: Free, 1: Monthly, 2: Yearly)
    let subscriptionTypeValue: number | undefined;
    if (values.subscriptionType === 0 || values.subscriptionType === 'Free') {
      subscriptionTypeValue = 0;
    } else if (values.subscriptionType === 1 || values.subscriptionType === 'Monthly') {
      subscriptionTypeValue = 1;
    } else if (values.subscriptionType === 2 || values.subscriptionType === 'Yearly') {
      subscriptionTypeValue = 2;
    }
    
    const payload = {
      ...values,
      subscriptionType: subscriptionTypeValue,
      subscriptionStartDate: values.subscriptionStartDate ? values.subscriptionStartDate.toISOString() : null,
      subscriptionEndDate: values.subscriptionEndDate ? values.subscriptionEndDate.toISOString() : null,
    };
    updateCompanyMutation.mutate({ id: selectedCompany.id, payload });
  };

  const getStatusTag = (status: number) => {
    switch (status) {
      case 0:
        return <Tag color="orange">Onay Bekliyor</Tag>;
      case 1:
        return <Tag color="green">Onaylandı</Tag>;
      case 2:
        return <Tag color="red">Reddedildi</Tag>;
      default:
        return <Tag>Bilinmiyor</Tag>;
    }
  };

  const getSubscriptionTag = (type: number) => {
    switch (type) {
      case 0:
        return <Tag color="blue">Ücretsiz</Tag>;
      case 1:
        return <Tag color="purple">Aylık</Tag>;
      case 2:
        return <Tag color="purple">Yıllık</Tag>;
      default:
        return <Tag>Bilinmiyor</Tag>;
    }
  };

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 70,
      fixed: 'left' as const,
      render: (logo: string) => (
        <Avatar 
          size={36} 
          src={logo ? `data:image/png;base64,${logo}` : undefined}
          icon={<BankOutlined />}
        />
      ),
    },
    {
      title: 'Firma Adı',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 180,
      fixed: 'left' as const,
      sorter: true,
      sortOrder: (sortBy === 'companyName' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      render: (text: string) => <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>{text}</Text>,
      ellipsis: true
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: true,
      sortOrder: (sortBy === 'email' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      ellipsis: true
    },
    {
      title: 'Telefon',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 130
    },
    {
      title: 'Durum',
      dataIndex: 'firmStatus',
      key: 'firmStatus',
      width: 120,
      render: (status: number) => getStatusTag(status),
    },
    {
      title: 'Abonelik',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      width: 120,
      render: (type: number) => getSubscriptionTag(type),
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      sortOrder: (sortBy === 'createdAt' ? (sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null) : null) as 'ascend' | 'descend' | null | undefined,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: AdminCompany) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            size="small"
            title="Detay"
          />
          {record.firmStatus === 1 && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              title="Düzenle"
            />
          )}
          {record.firmStatus === 0 && (
            <>
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record.id)}
                style={{ color: 'var(--success)' }}
                size="small"
                title="Onayla"
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => handleReject(record.id)}
                style={{ color: 'var(--error)' }}
                size="small"
                title="Reddet"
              />
            </>
          )}
          {record.firmStatus === 2 && (
            <Button
              type="text"
              icon={<ClockCircleOutlined />}
              onClick={() => handlePending(record.id)}
              style={{ color: 'var(--primary-500)' }}
              size="small"
              title="Beklemede Yap"
            />
          )}
        </Space>
      ),
    },
  ];

  // Frontend'de sadece searchTerm filtresi yapılıyor (isteğe bağlı, backend search yoksa)
  // Arama backend'de yapılıyor (searchTerm API'ye gidiyor)
  const filteredCompanies = companies;

  return (
    <div className="panel-tab-content">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 600 }}>Firma Talepleri</h2>
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Firma ara..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: '100%', maxWidth: '400px' }}
            size="large"
          />
        </div>
      </div>

      <TabListLoading loading={loading}>
      <Card className="panel-table-card" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <Tabs activeKey={activeTab} onChange={(key) => {
          setActiveTab(key);
          setCurrentPage(1);
        }}>
          <TabPane tab="Onay Bekleyenler" key="pending">
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={filteredCompanies}
              loading={loading}
              rowKey="id"
              onChange={handleTableChange}
              size="middle"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} firma`,
                onChange: handlePageChange,
                onShowSizeChange: handlePageChange,
                pageSizeOptions: ['10', '20', '50', '100'],
                style: { marginTop: '16px' }
              }}
            />
          </TabPane>
          <TabPane tab="Onaylananlar" key="approved">
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={filteredCompanies}
              loading={loading}
              rowKey="id"
              onChange={handleTableChange}
              size="middle"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} firma`,
                onChange: handlePageChange,
                onShowSizeChange: handlePageChange,
                pageSizeOptions: ['10', '20', '50', '100'],
                style: { marginTop: '16px' }
              }}
            />
          </TabPane>
          <TabPane tab="Reddedilenler" key="rejected">
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={filteredCompanies}
              loading={loading}
              rowKey="id"
              onChange={handleTableChange}
              size="middle"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} firma`,
                onChange: handlePageChange,
                onShowSizeChange: handlePageChange,
                pageSizeOptions: ['10', '20', '50', '100'],
                style: { marginTop: '16px' }
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
      </TabListLoading>

      {/* Detay Modal */}
      <Modal
        title="Firma Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={800}
      >
        {selectedCompany && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  {selectedCompany.logo ? (
                    <Avatar 
                      size={80} 
                      src={`data:image/png;base64,${selectedCompany.logo}`}
                      icon={<BankOutlined />}
                    />
                  ) : (
                    <Avatar size={80} icon={<BankOutlined />} />
                  )}
                  <Title level={3} style={{ marginTop: '16px' }}>
                    {selectedCompany.companyName}
                  </Title>
                  {getStatusTag(selectedCompany.firmStatus)}
                </div>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Text strong>E-posta:</Text>
                <div>{selectedCompany.email}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Telefon:</Text>
                <div>{selectedCompany.phoneNumber}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Vergi No:</Text>
                <div>{selectedCompany.taxNumber}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Yetkili Kişi:</Text>
                <div>{selectedCompany.authorizedPerson}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Website:</Text>
                <div>{selectedCompany.website || 'Belirtilmemiş'}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Şirket Büyüklüğü:</Text>
                <div>{selectedCompany.companySize || 'Belirtilmemiş'}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Sektör:</Text>
                <div>{selectedCompany.industry || 'Belirtilmemiş'}</div>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong>Abonelik:</Text>
                <div>{getSubscriptionTag(selectedCompany.subscriptionType)}</div>
              </Col>
              <Col xs={24}>
                <Text strong>Adres:</Text>
                <div>{selectedCompany.address || 'Belirtilmemiş'}</div>
              </Col>
              <Col xs={24}>
                <Text strong>Açıklama:</Text>
                <div>{selectedCompany.description || 'Belirtilmemiş'}</div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Düzenleme Modal */}
      <Modal
        title="Firma Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item name="companyName" label="Firma Adı">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="E-posta">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phoneNumber" label="Telefon">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="website" label="Website">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="companySize" label="Şirket Büyüklüğü">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="industry" label="Sektör">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="address" label="Adres">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Açıklama">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item name="subscriptionType" label="Abonelik Türü">
                <Select>
                  <Option value={0}>Ücretsiz</Option>
                  <Option value={1}>Aylık</Option>
                  <Option value={2}>Yıllık</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isSubscriptionActive" label="Abonelik Aktif" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const subType = form.getFieldValue('subscriptionType');
                  if (subType === 1 || subType === 2) {
                    return (
                      <>
                        <Form.Item name="subscriptionStartDate" label="Abonelik Başlangıç Tarihi">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="subscriptionEndDate" label="Abonelik Bitiş Tarihi">
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyListTab;
